import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ArticleSchemaProps {
  headline: string;
  description: string;
  author?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  articleSection?: string;
  keywords?: string[];
}

const ArticleSchema = ({
  headline,
  description,
  author = "Guestly Homes",
  datePublished = "2024-01-01T00:00:00Z",
  dateModified = new Date().toISOString(),
  image = "https://guestlyhomes.com/favicon.svg",
  articleSection = "Corporate Housing",
  keywords = ["corporate housing", "business travel", "furnished apartments"]
}: ArticleSchemaProps) => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    // Remove existing article schema if present
    const existingSchema = document.querySelector('script[data-schema="article"]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    // Create Article schema
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": headline,
      "description": description,
      "image": image,
      "author": {
        "@type": "Organization",
        "name": author,
        "url": "https://guestlyhomes.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Guestly Homes",
        "logo": {
          "@type": "ImageObject",
          "url": "https://guestlyhomes.com/favicon.svg"
        }
      },
      "datePublished": datePublished,
      "dateModified": dateModified,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      },
      "articleSection": articleSection,
      "keywords": keywords.join(", "),
      "inLanguage": i18n.language === 'sv' ? 'sv-SE' : 'en-US',
      "isAccessibleForFree": "True",
      "wordCount": description.split(' ').length
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'article');
    script.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(script);
    
    return () => {
      const schemaToRemove = document.querySelector('script[data-schema="article"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, [headline, description, author, datePublished, dateModified, image, articleSection, keywords, i18n.language]);
  
  return null;
};

export default ArticleSchema;
